import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { orgLabelPostProcessor } from "./orgLabel";

/**
 * The translations are not in the main bundle: each language is a chunk of
 * its own, fetched when first needed. Bundled, the two were 1.6 MB of the
 * login page's JavaScript (webpack writes every Arabic letter as \uXXXX).
 *
 * - the active language loads before the first render: `index.js` renders
 *   once `i18nReady` settles, so no raw key ever shows.
 * - the other one loads on a switch: `i18n.changeLanguage` fetches it before
 *   it changes anything, so the page stays in the old language until then.
 * - English pulls Arabic in too, as its fallback, exactly as before.
 * - something that needs both languages at once (a bilingual printout) asks
 *   with `i18n.loadLanguages(["ar", "en"])` first — see print/PrintSheet.jsx.
 */
const LOCALES = {
  ar: () => import(/* webpackChunkName: "locale-ar" */ "./locales/ar/translation.json"),
  en: () => import(/* webpackChunkName: "locale-en" */ "./locales/en/translation.json"),
};

const localeChunks = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    const load = LOCALES[language];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, module.default || module),
      // `true`: worth a retry — a dropped connection, not a missing language.
      (error) => callback(error, true)
    );
  },
};

/** The language LanguageContext will show, read the same way, so only it loads first. */
function startLanguage() {
  try {
    return localStorage.getItem("preferredLanguage") === "en" ? "en" : "ar";
  } catch (e) {
    return "ar";
  }
}

export const i18nReady = i18n
  // Load each language from its own chunk
  .use(localeChunks)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Resolve per-tenant institution-noun tokens ({org}/{org_indef})
  .use(orgLabelPostProcessor)
  // Initialize i18next
  .init({
    // Run the org-label resolver on every translation (fast-paths non-tokens).
    postProcess: ["orgLabel"],
    // The stored choice (Arabic by default), so the first render is already in it
    lng: startLanguage(),
    // Fallback language set to Arabic
    fallbackLng: "ar",

    // Language detection options
    detection: {
      // Only check localStorage - don't auto-detect from browser
      order: ["localStorage"],
      caches: ["localStorage"],
      // Default to Arabic if no language detected
      lookupLocalStorage: "preferredLanguage",
      // Default language when nothing is found
      defaultLanguage: "ar",
    },

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React options
    react: {
      useSuspense: false,
    },

    // Development options
    debug: process.env.NODE_ENV === "development",
  });

export default i18n;
